/**
 * The voice session as a pure state machine. The controller feeds it
 * actions; the UI reads it. Honest states (rule 2):
 *
 *   idle ─join→ requesting (token) ─token→ signalling ─welcome→ connecting
 *     connecting ─media connected→ connected
 *     connected ─media lost / socket closed→ failed (with a reason)
 *     any ─leave→ idle
 *
 * "Connected" needs both the engine's `welcome` and a connected media
 * path. A talk ring means "transmitting" (2026-10-28): yours while
 * push-to-talk is held or voice activity is open, someone else's while
 * comms-voice says they transmit to you (`speaking {pid, on}`). The roster
 * holds one entry per person.
 */
import {
  VoiceCloseCodes,
  type Pid,
  type Snowflake,
  type VoiceMediaSource,
  type VoiceRegionId,
  type VoiceMember,
  type VoicePublishCaps,
  type VoiceVideoRef,
} from '@neosocial/protocol';
import {
  clampVolume,
  type LevelsEvent,
  type MediaState,
  type PeerLevel,
  type RosterMember,
  type ServerMessage,
} from './types';

/**
 * One of your publications (screen share or camera,
 * docs/decisions/2026-10-29-screen-share-and-video.md): `starting` while
 * comms-voice is asked (`publish`), `live` from `published` until it ends.
 */
export interface LivePub {
  state: 'starting' | 'live';
  /** A screen with its sound. */
  audio: boolean;
  /** The caps it holds to (from `published`). */
  caps: VoicePublishCaps | null;
  /** Someone receives it now (`send`): the encoder runs; paused otherwise. */
  sending: boolean;
}

export type VoiceStatus =
  'idle' | 'requesting' | 'signalling' | 'connecting' | 'connected' | 'failed';

export type VoiceError =
  | 'password_required'
  | 'bad_password'
  | 'not_allowed'
  | 'full'
  | 'banned'
  | 'no_server'
  | 'auth'
  | 'media'
  | 'lost'
  | 'kicked'
  | 'replaced'
  /** You disconnected this connection yourself, from another device or tab. */
  | 'left_elsewhere'
  /**
   * A role or overwrite change took your channel away (Connect or View
   * Channel) and there was nowhere to move you: not a moderator.
   */
  | 'lost_access'
  | 'unknown';

export interface VoiceSession {
  status: VoiceStatus;
  /** Where you are (confirmed by the engine). */
  channelId: Snowflake | null;
  /** Where you asked to go (shown while connecting or moving). */
  targetId: Snowflake | null;
  guildId: Snowflake | null;
  pid: Pid | null;
  /** Everyone in voice you may see, by pid (the live roster), with their live media (`media`). */
  members: Record<Pid, RosterMember>;
  /** Who watches each live screen in your channel (`viewers`), by the streamer's pid. */
  viewers: Record<Pid, Pid[]>;
  /** The screens you watch now (`watch_set`). */
  watching: Record<Pid, true>;
  /** Your own screen share and camera. */
  live: { screen: LivePub | null; camera: LivePub | null };
  /** What each of your video slots carries now (`vslot`), by mid. */
  vslots: Record<string, VoiceVideoRef>;
  /** Whose screen sound each stream-audio slot carries now (`sslot`), by mid. */
  sslots: Record<string, Pid>;
  /** Receive slot -> who it carries. */
  slots: Record<number, { pid: Pid }>;
  /**
   * pids transmitting to you now, from comms-voice's `speaking {pid, on}`
   * (push-to-talk held or voice activity open). Others' talk rings follow
   * this, as Discord's speaking op and TeamSpeak's talk indicator do. The
   * audio levels light nothing, so the session keeps none of them: a
   * level tick (10 a second) that changed the session re-rendered every
   * screen that reads it (voice performance, 2026-11-01).
   */
  talking: Record<Pid, true>;
  /**
   * pids transmitting to you as a priority speaker now (`speaking {pid, on,
   * priority}`, 2026-11-01): everyone else is lowered while one talks.
   * Read together with `talking` (`priorityPids`).
   */
  priority: Record<Pid, true>;
  /** You hold the priority-speaker key (and may be one in this channel). */
  mePriority: boolean;
  /** You are transmitting: the gate is open, unmuted, with a microphone. */
  meSpeaking: boolean;
  selfMute: boolean;
  selfDeaf: boolean;
  /** The PTT / voice-activity gate. */
  transmitting: boolean;
  rttMs: number | null;
  /**
   * The packets lost on the way to you since this connection began, as a
   * percentage of all sent (every slot's, to one decimal); null until any
   * arrived. The connection popover on the dock's signal icon shows it.
   */
  lossPct: number | null;
  listenOnly: boolean;
  /** The saved microphone is gone: the system default one stands in (HV-B07). */
  micFallback: boolean;
  /**
   * The connection dropped and comms-voice is being reached again by itself
   * (attempt number; 0 when not reconnecting). HV-B01.
   */
  reconnecting: number;
  error: VoiceError | null;
  errorMessage: string | null;
  /** userId -> locally muted (listener mute). Survives rejoining. */
  localMutes: Record<Snowflake, boolean>;
  /** userId -> volume 0..2 (above 1 boosts). Survives rejoining. */
  volumes: Record<Snowflake, number>;
  /**
   * The voice region the channel runs in, from the token (2026-11-03);
   * null from a server without regions. `regionTemporary`: its own
   * region's node is not reachable, so it runs in the default one for now.
   */
  region: VoiceRegionId | null;
  regionTemporary: boolean;
}

export const initialVoice = (
  keep?: Pick<VoiceSession, 'localMutes' | 'volumes' | 'selfMute' | 'selfDeaf'>,
): VoiceSession => ({
  status: 'idle',
  channelId: null,
  targetId: null,
  guildId: null,
  pid: null,
  members: {},
  viewers: {},
  watching: {},
  live: { screen: null, camera: null },
  vslots: {},
  sslots: {},
  slots: {},
  talking: {},
  priority: {},
  mePriority: false,
  meSpeaking: false,
  selfMute: keep?.selfMute ?? false,
  selfDeaf: keep?.selfDeaf ?? false,
  transmitting: false,
  rttMs: null,
  lossPct: null,
  listenOnly: false,
  micFallback: false,
  reconnecting: 0,
  error: null,
  errorMessage: null,
  localMutes: keep?.localMutes ?? {},
  volumes: keep?.volumes ?? {},
  region: null,
  regionTemporary: false,
});

export type VoiceAction =
  | { type: 'join'; channelId: Snowflake; guildId: Snowflake }
  | { type: 'token'; region?: VoiceRegionId | null; regionTemporary?: boolean }
  /** The channel's region is the same, its "(temporary)" changed (NX_VOICE_REGION_MOVE). */
  | { type: 'region'; temporary: boolean }
  | {
      type: 'welcome';
      pid: Pid;
      channel: Snowflake;
      members: RosterMember[];
      listenOnly: boolean;
      micFallback?: boolean;
    }
  /** You asked to go live or turn the camera on (`publish` on its way). */
  | { type: 'pub_start'; source: VoiceMediaSource; audio: boolean }
  /** Your publication ended here (stopped, refused, or the capture ended). */
  | { type: 'pub_end'; source: VoiceMediaSource }
  /** The connection dropped: reaching comms-voice again, attempt `attempt` (HV-B01). */
  | { type: 'reconnecting'; attempt: number; channelId: Snowflake }
  /** A microphone was picked mid-call (HV-B08). */
  | { type: 'mic'; listenOnly: boolean; fallback: boolean }
  | { type: 'media'; state: MediaState }
  | { type: 'server'; msg: ServerMessage }
  | { type: 'levels'; ev: LevelsEvent; now: number }
  | { type: 'self'; mute?: boolean; deaf?: boolean }
  | { type: 'transmit'; on: boolean }
  /** The priority-speaker key went down or up (2026-11-01). */
  | { type: 'priority'; on: boolean }
  | { type: 'moving'; channelId: Snowflake }
  | { type: 'move_failed' }
  | { type: 'error'; error: VoiceError; message?: string }
  | { type: 'local_mute'; userId: Snowflake; muted: boolean }
  | { type: 'volume'; userId: Snowflake; volume: number }
  | { type: 'leave' };

function fail(s: VoiceSession, error: VoiceError, message?: string): VoiceSession {
  return {
    ...initialVoice(s),
    status: 'failed',
    error,
    errorMessage: message ?? null,
    targetId: s.targetId ?? s.channelId,
    guildId: s.guildId,
  };
}

/**
 * The packets lost on the way to you, as a percentage of all sent (lost
 * and received, every slot's counts since the connection began), to one
 * decimal; null while nothing has arrived.
 */
export function lossPercent(peers: readonly PeerLevel[]): number | null {
  let got = 0;
  let lost = 0;
  for (const p of peers) {
    got += Math.max(0, p.packets);
    lost += Math.max(0, p.lost);
  }
  if (got + lost <= 0) return null;
  return Math.round((lost / (got + lost)) * 1000) / 10;
}

/** How the connection is, by its round trip and loss: the dock's signal icon colour. */
export type SignalQuality = 'good' | 'ok' | 'bad';

/** Round trips above these (ms) are "ok", then "bad". */
export const PING_OK_MS = 150;
export const PING_BAD_MS = 300;
/** Packet loss above these (%) is "ok", then "bad". */
export const LOSS_OK_PCT = 2;
export const LOSS_BAD_PCT = 10;

/** The signal's quality, the worse of the ping and the loss; null until the ping is measured. */
export function signalQuality(rttMs: number | null, lossPct: number | null): SignalQuality | null {
  if (rttMs === null) return null;
  const loss = lossPct ?? 0;
  if (rttMs > PING_BAD_MS || loss > LOSS_BAD_PCT) return 'bad';
  if (rttMs > PING_OK_MS || loss > LOSS_OK_PCT) return 'ok';
  return 'good';
}

export function voiceReducer(s: VoiceSession, a: VoiceAction): VoiceSession {
  switch (a.type) {
    case 'join':
      return {
        ...initialVoice(s),
        status: 'requesting',
        targetId: a.channelId,
        guildId: a.guildId,
      };
    case 'token':
      return s.status === 'requesting'
        ? {
            ...s,
            status: 'signalling',
            region: a.region ?? null,
            regionTemporary: !!a.regionTemporary,
          }
        : s;
    case 'region':
      return s.regionTemporary === a.temporary ? s : { ...s, regionTemporary: a.temporary };
    case 'welcome': {
      const members = onePerUser(a.members, a.pid);
      return {
        ...s,
        status: s.status === 'connected' ? 'connected' : 'connecting',
        pid: a.pid,
        channelId: a.channel,
        targetId: null,
        members,
        // a new connection: comms-voice holds no watch, slot or publication of ours
        viewers: {},
        watching: {},
        live: { screen: null, camera: null },
        vslots: {},
        sslots: {},
        listenOnly: a.listenOnly,
        micFallback: !!a.micFallback,
        meSpeaking: transmittingNow({ ...s, listenOnly: a.listenOnly }),
        error: null,
        errorMessage: null,
      };
    }
    case 'pub_start':
      if (s.pid === null) return s;
      return {
        ...s,
        live: {
          ...s.live,
          [a.source]: { state: 'starting', audio: a.audio, caps: null, sending: false },
        },
      };
    case 'pub_end':
      return s.live[a.source] ? { ...s, live: { ...s.live, [a.source]: null } } : s;
    case 'reconnecting':
      return {
        ...initialVoice(s),
        status: 'requesting',
        targetId: a.channelId,
        guildId: s.guildId,
        reconnecting: a.attempt,
      };
    case 'mic': {
      // a microphone picked while connected: listening only ends (ST-B05, HV-B08)
      if (s.status === 'idle') return s;
      const next = { ...s, listenOnly: a.listenOnly, micFallback: a.fallback };
      return { ...next, meSpeaking: transmittingNow(next) };
    }
    case 'media':
      if (a.state === 'connected')
        return s.pid !== null && s.status === 'connecting'
          ? { ...s, status: 'connected', reconnecting: 0 }
          : s;
      if (a.state === 'failed')
        return s.status === 'idle' ? s : fail(s, s.status === 'connected' ? 'lost' : 'media');
      if (a.state === 'closed')
        return s.status === 'connected' || s.status === 'connecting' ? fail(s, 'lost') : s;
      return s;
    case 'server':
      return onServer(s, a.msg);
    case 'levels': {
      // Ten ticks a second: the session changes only when what the screens
      // show does, the round trip in whole ms (the dock's "· 12 ms") and the
      // packet loss to one decimal (its connection popover). Not measured
      // yet: the last ones stay.
      if (s.status === 'idle') return s;
      const rtt = a.ev.rtt_ms === null ? s.rttMs : Math.round(a.ev.rtt_ms);
      const loss = lossPercent(a.ev.peers) ?? s.lossPct;
      return rtt === s.rttMs && loss === s.lossPct ? s : { ...s, rttMs: rtt, lossPct: loss };
    }
    case 'self': {
      const selfDeaf = a.deaf ?? s.selfDeaf;
      // Discord: a deafen silences your microphone too, and undeafening
      // gives back the mute you had before. The deafen's own silence is
      // `selfDeaf` (transmittingNow, the mic button), never written into
      // `selfMute`, so `selfMute` stays what you chose: undeafening after
      // mute + deafen leaves you muted (QA 2026-10-28 item 7: it unmuted).
      // A click on the mic while deafened says `mute: false` itself
      // (controller.toggleMute).
      const selfMute = a.mute ?? s.selfMute;
      const next = { ...s, selfMute, selfDeaf };
      return { ...next, meSpeaking: transmittingNow(next) };
    }
    case 'transmit': {
      const next = { ...s, transmitting: a.on };
      return { ...next, meSpeaking: transmittingNow(next) };
    }
    case 'priority':
      return s.mePriority === a.on ? s : { ...s, mePriority: a.on };
    case 'moving':
      return { ...s, targetId: a.channelId };
    case 'move_failed':
      return { ...s, targetId: null };
    case 'error':
      return fail(s, a.error, a.message);
    case 'local_mute':
      return { ...s, localMutes: { ...s.localMutes, [a.userId]: a.muted } };
    case 'volume':
      return { ...s, volumes: { ...s.volumes, [a.userId]: clampVolume(a.volume) } };
    case 'leave':
      return initialVoice(s);
  }
}

/**
 * One entry per person (live bug 2026-10-28: a rejoin left your old
 * connection in the roster, so "you" showed once per rejoin). Your own entry
 * is only your own pid; anyone else's newest connection (highest pid) wins.
 */
export function onePerUser<M extends VoiceMember>(list: M[], myPid: Pid | null): Record<Pid, M> {
  const me = myPid === null ? undefined : list.find((m) => m.pid === myPid)?.user_id;
  const byUser = new Map<Snowflake, M>();
  for (const m of list) {
    if (me !== undefined && m.user_id === me && m.pid !== myPid) continue;
    const prev = byUser.get(m.user_id);
    if (!prev || m.pid > prev.pid) byUser.set(m.user_id, m);
  }
  const out: Record<Pid, M> = {};
  for (const m of byUser.values()) out[m.pid] = m;
  return out;
}

/** Removes a pid and everything keyed by it. */
function dropPid(s: VoiceSession, pid: Pid): VoiceSession {
  if (!(pid in s.members) && !(pid in s.talking)) return s;
  const members = { ...s.members };
  delete members[pid];
  const talking = { ...s.talking };
  delete talking[pid];
  let priority = s.priority;
  if (pid in priority) {
    priority = { ...priority };
    delete priority[pid];
  }
  const slots: VoiceSession['slots'] = {};
  for (const [k, v] of Object.entries(s.slots)) if (v.pid !== pid) slots[Number(k)] = v;
  // their stream, their watch of others, and the slots that carried them
  const viewers: VoiceSession['viewers'] = {};
  for (const [k, list] of Object.entries(s.viewers))
    if (Number(k) !== pid) viewers[Number(k)] = list.filter((p) => p !== pid);
  const watching = { ...s.watching };
  delete watching[pid];
  const vslots: VoiceSession['vslots'] = {};
  for (const [k, v] of Object.entries(s.vslots)) if (v.pid !== pid) vslots[k] = v;
  const sslots: VoiceSession['sslots'] = {};
  for (const [k, v] of Object.entries(s.sslots)) if (v !== pid) sslots[k] = v;
  return { ...s, members, talking, priority, slots, viewers, watching, vslots, sslots };
}

function onServer(s: VoiceSession, m: ServerMessage): VoiceSession {
  switch (m.t) {
    case 'member': {
      // another connection of yours (an old one being replaced): never a second "you"
      const myUser = s.pid !== null ? s.members[s.pid]?.user_id : undefined;
      if (m.pid !== s.pid && myUser !== undefined && m.user_id === myUser) return s;
      // a person who connected again: their older pid goes
      for (const o of Object.values(s.members))
        if (o.user_id === m.user_id && o.pid !== m.pid && o.pid < m.pid) s = dropPid(s, o.pid);
      if (Object.values(s.members).some((o) => o.user_id === m.user_id && o.pid > m.pid)) return s;
      const prev = s.members[m.pid];
      const member: RosterMember = prev
        ? { ...prev, user_id: m.user_id, name: m.name, channel: m.channel }
        : {
            pid: m.pid,
            user_id: m.user_id,
            name: m.name,
            channel: m.channel,
            self_mute: false,
            self_deaf: false,
            mute: false,
            deaf: false,
            server_mute: false,
          };
      const next = { ...s, members: { ...s.members, [m.pid]: member } };
      // a moderator moved you
      if (m.pid === s.pid) next.channelId = m.channel;
      return next;
    }
    case 'left':
      return dropPid(s, m.pid);
    case 'resumed': {
      // the same connection on a new socket (2026-11-03): the roster as
      // comms-voice has it now; what was missed meanwhile follows
      const members = onePerUser(m.members, s.pid);
      for (const p of Object.keys(s.members)) if (!(p in members)) s = dropPid(s, Number(p));
      return { ...s, channelId: m.channel, members };
    }
    case 'speaking': {
      // a priority speaker (2026-11-01): `on` again when it changes mid-talk
      const prio = m.on && !!m.priority;
      if (!!s.talking[m.pid] === m.on && !!s.priority[m.pid] === prio) return s;
      const talking = { ...s.talking };
      if (m.on) talking[m.pid] = true;
      else delete talking[m.pid];
      // the same object while it does not change: a plain speaking edge
      // changes `talking` alone (useVoiceQuiet)
      let priority = s.priority;
      if (!!s.priority[m.pid] !== prio) {
        priority = { ...s.priority };
        if (prio) priority[m.pid] = true;
        else delete priority[m.pid];
      }
      return { ...s, talking, priority };
    }
    case 'slot':
      return { ...s, slots: { ...s.slots, [m.slot]: { pid: m.pid } } };
    case 'mute': {
      const prev = s.members[m.pid];
      if (!prev) return s;
      const member: RosterMember = {
        ...prev,
        self_mute: m.self_mute,
        self_deaf: m.self_deaf,
        mute: m.mute ?? prev.mute,
        deaf: m.deaf ?? prev.deaf,
        server_mute: m.server_mute ?? prev.server_mute,
        // a moderator's channel mute / deafen where they are now (2026-10-28)
        channel_mute: m.channel_mute ?? prev.channel_mute,
        channel_deaf: m.channel_deaf ?? prev.channel_deaf,
      };
      const next = { ...s, members: { ...s.members, [m.pid]: member } };
      // Nobody hears a muted person (a server mute or deafen, a channel mute
      // or deafen there, can't speak here, or their own mute or deafen), so
      // no ring: comms-voice says `speaking off` too, and `on` again after
      // the undo while they transmit. This holds even against a comms-voice
      // that does not (live bug 2026-10-28: the ring stayed on after a
      // server mute).
      if (
        (member.mute ||
          member.deaf ||
          member.channel_mute ||
          member.channel_deaf ||
          member.self_mute ||
          member.self_deaf) &&
        s.talking[m.pid]
      ) {
        const talking = { ...s.talking };
        delete talking[m.pid];
        next.talking = talking;
      }
      return next;
    }
    case 'moved': {
      const members = { ...s.members };
      if (s.pid !== null && members[s.pid])
        members[s.pid] = { ...members[s.pid]!, channel: m.channel };
      // rings of people in the room you left go dark (comms-voice also says
      // `off`); rings already lit in the new room stay (HV-B09)
      let talking = s.talking;
      for (const pid of Object.keys(s.talking)) {
        if (members[Number(pid)]?.channel === m.channel) continue;
        if (talking === s.talking) talking = { ...s.talking };
        delete talking[Number(pid)];
      }
      // comms-voice ended your watches and slots before the move (§4.6); the
      // viewer lists of the room you left say nothing here any more
      const viewers: VoiceSession['viewers'] = {};
      for (const [k, list] of Object.entries(s.viewers))
        if (members[Number(k)]?.channel === m.channel) viewers[Number(k)] = list;
      return {
        ...s,
        channelId: m.channel,
        targetId: null,
        members,
        talking,
        watching: {},
        viewers,
        vslots: {},
        sslots: {},
      };
    }
    case 'media': {
      const prev = s.members[m.pid];
      if (!prev) return s;
      if (
        !!prev.screen === m.screen &&
        !!prev.screen_audio === m.screen_audio &&
        !!prev.camera === m.camera
      )
        return s;
      const member: RosterMember = {
        ...prev,
        screen: m.screen,
        screen_audio: m.screen_audio,
        camera: m.camera,
      };
      const next = { ...s, members: { ...s.members, [m.pid]: member } };
      if (!m.screen) {
        // their stream ended: no viewers, nobody watches it
        if (s.viewers[m.pid]) {
          next.viewers = { ...s.viewers };
          delete next.viewers[m.pid];
        }
        if (s.watching[m.pid]) {
          next.watching = { ...s.watching };
          delete next.watching[m.pid];
        }
      }
      return next;
    }
    case 'viewers': {
      const viewers = { ...s.viewers };
      if (m.viewers.length) viewers[m.pid] = m.viewers;
      else delete viewers[m.pid];
      return { ...s, viewers };
    }
    case 'watch_set': {
      if (!!s.watching[m.pid] === m.on) return s;
      const watching = { ...s.watching };
      if (m.on) watching[m.pid] = true;
      else delete watching[m.pid];
      return { ...s, watching };
    }
    case 'vslot': {
      const vslots = { ...s.vslots };
      if (m.pid === null || m.pid === undefined) delete vslots[m.mid];
      else vslots[m.mid] = { pid: m.pid, source: m.source ?? 'camera' };
      return { ...s, vslots };
    }
    case 'sslot': {
      const sslots = { ...s.sslots };
      if (m.pid === null || m.pid === undefined) delete sslots[m.mid];
      else sslots[m.mid] = m.pid;
      return { ...s, sslots };
    }
    case 'published': {
      if (!m.on) return s.live[m.source] ? { ...s, live: { ...s.live, [m.source]: null } } : s;
      const prev = s.live[m.source];
      // a late answer after you stopped it again: not live
      if (!prev) return s;
      return {
        ...s,
        live: { ...s.live, [m.source]: { ...prev, state: 'live', caps: m.caps ?? prev.caps } },
      };
    }
    case 'unpublished':
      return s.live[m.source] ? { ...s, live: { ...s.live, [m.source]: null } } : s;
    case 'send': {
      const prev = s.live[m.source];
      if (!prev || prev.sending === m.video) return s;
      return { ...s, live: { ...s.live, [m.source]: { ...prev, sending: m.video } } };
    }
    case 'bye':
      return fail(s, errorFromBye(m.reason), m.message);
    default:
      return s;
  }
}

/**
 * The pids transmitting to you as a priority speaker now (2026-11-01): told
 * `on` with `priority`, and still talking.
 */
export function priorityPids(s: Pick<VoiceSession, 'talking' | 'priority'>): Pid[] {
  return Object.keys(s.priority)
    .map(Number)
    .filter((p) => !!s.talking[p]);
}

/** Is this user talking as a priority speaker now (you: holding the key, and heard)? */
export function prioritySpeaking(
  s: VoiceSession,
  userId: Snowflake,
  myUserId: Snowflake | undefined,
): boolean {
  if (userId === myUserId) return s.mePriority && s.meSpeaking && !myVoiceBlocked(s);
  // deafened, you see nobody talking (2026-11-01)
  if (myDeaf(s)) return false;
  return priorityPids(s).some((p) => s.members[p]?.user_id === userId);
}

/**
 * The volume each person plays at while a priority speaker talks (Discord
 * lowers everyone else): `base` times `duck` for everyone in `pids` but
 * the priority speakers themselves; null for nobody lowered.
 */
export function duckedPids(
  s: Pick<VoiceSession, 'talking' | 'priority' | 'members'>,
): Set<Pid> | null {
  const prio = priorityPids(s);
  if (prio.length === 0) return null;
  const out = new Set<Pid>();
  for (const k of Object.keys(s.members)) {
    const p = Number(k);
    if (!prio.includes(p)) out.add(p);
  }
  return out;
}

/**
 * True when comms-voice's roster marks you `mute`: a server mute, or no right
 * to speak in this channel. Nobody hears you then, so no speaking ring.
 */
export function myServerMute(s: VoiceSession): boolean {
  return s.pid !== null && !!s.members[s.pid]?.mute;
}

/**
 * True when comms-voice forwards nothing from you whatever you toggle: the
 * roster's `mute` (`myServerMute`), a moderator's server deafen, which
 * silences you too (Discord's Deafen Members, 2026-10-28), or a moderator's
 * channel mute or deafen in the channel you are in (2026-10-28). No
 * speaking ring on yourself then.
 */
export function myVoiceBlocked(s: VoiceSession): boolean {
  const me = s.pid !== null ? s.members[s.pid] : undefined;
  return !!me && (me.mute || me.deaf || !!me.channel_mute || !!me.channel_deaf);
}

/**
 * You hear nobody now: your own deafen, a moderator's server deafen, or a
 * moderator's channel deafen where you are. comms-voice then sends you no
 * voice and no `speaking` (2026-11-01).
 */
export function myDeaf(s: Pick<VoiceSession, 'selfDeaf' | 'pid' | 'members'>): boolean {
  if (s.selfDeaf) return true;
  const me = s.pid !== null ? s.members[s.pid] : undefined;
  return !!me && (me.deaf || !!me.channel_deaf);
}

/**
 * Is this person's talk ring lit for you? Yours while you transmit and
 * someone may hear you; anyone else's while comms-voice says they transmit
 * to you. While you are deafened nobody's is (Discord: a deafened user
 * hears nobody and sees nobody talking until they undeafen).
 */
export function userSpeaking(
  s: VoiceSession,
  userId: Snowflake,
  myUserId: Snowflake | undefined,
): boolean {
  if (userId === myUserId) return s.meSpeaking && !myVoiceBlocked(s);
  if (myDeaf(s)) return false;
  for (const pidStr of Object.keys(s.talking)) {
    const m = s.members[Number(pidStr)];
    if (m && m.user_id === userId) return true;
  }
  return false;
}

/**
 * You are transmitting: push-to-talk held or the voice-activity gate open,
 * not muted or deafened, with a microphone. Your ring follows this, not the
 * audio level (live bug 2026-10-28: holding PTT lit nothing until you spoke).
 */
export function transmittingNow(
  s: Pick<VoiceSession, 'transmitting' | 'selfMute' | 'selfDeaf' | 'listenOnly'>,
): boolean {
  return s.transmitting && !s.selfMute && !s.selfDeaf && !s.listenOnly;
}

/**
 * The live roster as the screens show it: one entry per person, yours only
 * under your own pid (never an older connection of yours), in `channel` when
 * given.
 */
export function rosterPeople(
  s: Pick<VoiceSession, 'members' | 'pid'>,
  myUserId: Snowflake | undefined,
  channel?: Snowflake,
): VoiceMember[] {
  const seen = new Set<Snowflake>();
  const out: VoiceMember[] = [];
  const list = Object.values(s.members).sort((a, b) => b.pid - a.pid);
  for (const m of list) {
    if (channel !== undefined && m.channel !== channel) continue;
    if (m.user_id === myUserId && m.pid !== s.pid) continue;
    if (seen.has(m.user_id)) continue;
    seen.add(m.user_id);
    out.push(m);
  }
  return out;
}

/**
 * A person's entry in the live roster while you are in voice (yours only
 * under your own pid), or undefined: then the gateway's voice state is what
 * this client knows about them.
 */
export function rosterEntryOf(
  s: Pick<VoiceSession, 'members' | 'pid' | 'status'>,
  userId: Snowflake,
  myUserId: Snowflake | undefined,
): VoiceMember | undefined {
  if (s.pid === null || (s.status !== 'connected' && s.status !== 'connecting')) return undefined;
  if (userId === myUserId) return s.members[s.pid];
  let best: VoiceMember | undefined;
  for (const m of Object.values(s.members))
    if (m.user_id === userId && (!best || m.pid > best.pid)) best = m;
  return best;
}

/**
 * Is someone else in your voice channel now (the live roster, while you are
 * in voice)? Their Volume and "Mute for me" act on what you hear from them,
 * so a tile's open menu offers both only while this holds (QA 2026-10-28
 * round 3: they stayed after the person left voice).
 */
export function inMyVoiceChannel(
  s: Pick<VoiceSession, 'members' | 'pid' | 'status' | 'channelId'>,
  userId: Snowflake,
  myUserId: Snowflake | undefined,
): boolean {
  if (userId === myUserId || s.channelId === null) return false;
  return rosterEntryOf(s, userId, myUserId)?.channel === s.channelId;
}

export function pidOfUser(s: VoiceSession, userId: Snowflake): Pid | null {
  for (const m of Object.values(s.members)) if (m.user_id === userId) return m.pid;
  return null;
}

/**
 * Why the server closed your connection, from its `bye`. `left`: you
 * disconnected yourself from another device or tab, never "a moderator".
 * `lost_access`: a permission change took your channel away (2026-10-28),
 * never "a moderator" either.
 */
export function errorFromBye(reason: string): VoiceError {
  switch (reason) {
    case 'kicked':
      return 'kicked';
    case 'replaced':
      return 'replaced';
    case 'left':
      return 'left_elsewhere';
    case 'lost_access':
      return 'lost_access';
    case 'token_expired':
      return 'auth';
    default:
      return 'lost';
  }
}

/** The same from the close code alone (when no `bye` came first). */
export function errorFromClose(code: number): VoiceError {
  switch (code) {
    case VoiceCloseCodes.KICKED:
      return 'kicked';
    case VoiceCloseCodes.REPLACED:
      return 'replaced';
    case VoiceCloseCodes.LEFT:
      return 'left_elsewhere';
    case VoiceCloseCodes.LOST_ACCESS:
      return 'lost_access';
    // the channel moved to another voice node: rejoin there (never resume)
    case VoiceCloseCodes.MOVED_NODE:
      return 'lost';
    default:
      return 'lost';
  }
}

/** Maps a voice-token API error code or an engine deny reason to our error. */
export function errorFromDeny(reason: string): VoiceError {
  switch (reason) {
    case 'bad_password':
      return 'bad_password';
    case 'full':
      return 'full';
    case 'banned':
      return 'banned';
    case 'not_allowed':
    case 'not_joinable':
    case 'no_such_channel':
      return 'not_allowed';
    // the channel runs on another voice node (2026-11-03): a fresh token reaches it
    case 'room_on_other_node':
      return 'no_server';
    default:
      return 'unknown';
  }
}
